import { tw } from "@careinfo/tailwind";
import { useCallback, useMemo, useState } from "react";
import { Button } from "../../ui/Button";
import { Icon } from "../../ui/Icon";
import { Surface } from "../../ui/Surface";
import { Toggle } from "../../ui/Toggle";

export type LinkEditorPanelProps = {
  initialUrl?: string;
  initialOpenInNewTab?: boolean;
  onSetLink: (url: string, openInNewTab?: boolean) => void;
};

export const useLinkEditorState = ({
  initialUrl,
  initialOpenInNewTab,
  onSetLink,
}: LinkEditorPanelProps) => {
  const [url, setUrl] = useState(initialUrl || "");
  const [openInNewTab, setOpenInNewTab] = useState(
    initialOpenInNewTab || false
  );

  const onChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setUrl(event.target.value);
  }, []);

  const isValidUrl = useMemo(() => /^(\S+):(\/\/)?\S+$/.test(url), [url]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (isValidUrl) {
        onSetLink(url, openInNewTab);
      }
    },
    [url, isValidUrl, openInNewTab, onSetLink]
  );

  return {
    url,
    setUrl,
    openInNewTab,
    setOpenInNewTab,
    onChange,
    handleSubmit,
    isValidUrl,
  };
};

export const LinkEditorPanel = ({
  onSetLink,
  initialOpenInNewTab,
  initialUrl,
}: LinkEditorPanelProps) => {
  const state = useLinkEditorState({
    onSetLink,
    initialOpenInNewTab,
    initialUrl,
  });

  return (
    <Surface className={tw`p-2`}>
      <form
        onSubmit={state.handleSubmit}
        className={tw`flex items-center gap-2`}
      >
        {/* eslint-disable-next-line */}
        <label
          className={tw`flex items-center gap-2 p-2 rounded-lg bg-neutral-500 cursor-text`}
          htmlFor={"url"}
        >
          <Icon name="Link" className={tw`flex-none text-black`} />
          <input
            type="url"
            id="url"
            className={tw`flex-1 bg-transparent outline-none min-w-[12rem] text-black text-sm`}
            placeholder="Enter URL"
            value={state.url}
            onChange={state.onChange}
          />
        </label>
        <Button
          variant="primary"
          buttonSize="small"
          type="submit"
          disabled={!state.isValidUrl}
        >
          Set Link
        </Button>
      </form>
      <div className={tw`mt-3`}>
        {/* eslint-disable-next-line */}
        <label
          className={tw`flex items-center justify-start gap-2 text-sm font-semibold cursor-pointer select-none text-neutral-500`}
        >
          Open in new tab
          <Toggle
            active={state.openInNewTab}
            onChange={state.setOpenInNewTab}
          />
        </label>
      </div>
    </Surface>
  );
};
